import { createContext, useContext } from 'react'

import type { StatsigLocaleDynamicConfig } from '@src/config/localisation/statsig-locale-config.types'

/**
 * Per-request, Statsig-resolved locale config, threaded through the React tree.
 *
 * The value is `pageProps.localeConfig` — produced on the server by
 * `getServerSideLocaleConfig` (Statsig dynamic config merged over static defaults)
 * and provided at the app root (`_app`). Because it rides the React tree (not a
 * module-level store), it is available and identical on BOTH the SSR pass and the
 * client's first render — which is what keeps server HTML correct for crawlers /
 * no-JS clients and eliminates hydration mismatches.
 *
 * `undefined` on pages that don't run `getServerSideProps` (e.g. `_error` / 404);
 * `useStatsigDynamicLocaleConfig` falls back to the locale's static default there.
 *
 * NOTE: this is the per-request-safe replacement for reading the browser-only
 * `getLocaleConfigFromMemory()` inside the hook. The memory store remains for
 * non-React callers (`getIsCountryCompliant`, telemetry) and is unaffected.
 */
export const LocaleConfigContext = createContext<StatsigLocaleDynamicConfig | undefined>(undefined)

export const useLocaleConfigContext = (): StatsigLocaleDynamicConfig | undefined => useContext(LocaleConfigContext)
